/**
 * Pharmacy Detail Page
 * 
 * Comprehensive view of individual pharmacy with all related data including:
 * - State licensing coverage (geographic map visualization)
 * - Associated drug programs with pharmacy-specific pricing and configuration
 * 
 * ## Pharmacy-Program Relationship Documentation
 * 
 * The relationship between pharmacies and drug programs is complex and bidirectional:
 * 
 * ### Data Structure:
 * 1. **Pharmacy Schema** (`pharmacy` collection):
 *    - Contains basic pharmacy info (name, address, licensing, capacity)
 *    - `states[]` array defines geographic coverage/licensing
 *    - `pharmacy_config.enable_pharmacy` controls active status
 * 
 * 2. **Drug Program Schema** (`program_definition` collection):
 *    - Each program contains `pharmacies[]` array with pharmacy-specific configs
 *    - Each pharmacy entry includes pricing, drug specifications, and custom settings
 *    - Programs can be enabled/disabled per pharmacy independently
 * 
 * ### API Endpoints:
 * - `GET /api/pharmacy/:id` - Pharmacy details
 * - `GET /api/pharmacy/:id/programs` - Programs offered by this pharmacy
 *   Response: `{ data: { pharmacy: {...}, programs: [...] } }`
 * - `GET /api/drug-programs/:id/pharmacies` - Pharmacies offering this program
 *   Response: `{ data: { program: {...}, pharmacies: [...] } }`
 * 
 * ### Key Relationships:
 * - One pharmacy can offer many programs (1:N)
 * - One program can be offered by many pharmacies (N:1) 
 * - Each pharmacy-program pairing has unique pricing and configuration
 * - Geographic coverage (states) affects program availability
 * - Capacity limits affect program fulfillment capabilities
 */

import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { pharmacyAPI, drugProgramAPI, apiHelpers } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { canSeeDrugPrices } from '../lib/drugPricing'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  Button,
  Badge,
  Separator,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox
} from '../components/ui'
import PharmacyEditModal from '../components/PharmacyEditModal'
import CloneProgramModal from '../components/CloneProgramModal'
import {
  ComposableMap,
  Geographies,
  Geography
} from 'react-simple-maps'

// US states topology for react-simple-maps (using local file)
const geoUrl = "/gz_2010_us_040_00_20m.json"

export default function PharmacyDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, currentClinicId } = useAuthStore()
  const showPrices = canSeeDrugPrices(user)
  
  const [pharmacy, setPharmacy] = useState(null)
  const [programs, setPrograms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hoveredState, setHoveredState] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTag, setSelectedTag] = useState('')
  const [showDeaOnly, setShowDeaOnly] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [cloneProgramModal, setCloneProgramModal] = useState({ isOpen: false, program: null })
  
  // Mock available pharmacies for cloning (TODO: fetch from API)
  const [availablePharmacies] = useState([
    { id: '1', pharmacy_name: 'Regional Compound Pharmacy', pharmacy_ncpdpid: 1234567, pharmacy_gateway: '5Axis', states: ['CA', 'NV', 'AZ'] },
    { id: '2', pharmacy_name: 'National Chain Pharmacy', pharmacy_ncpdpid: 2345678, pharmacy_gateway: 'SelfShip', states: ['TX', 'FL', 'NY'] },
    { id: '3', pharmacy_name: 'Specialty Weight Loss Pharmacy', pharmacy_ncpdpid: 3456789, pharmacy_gateway: 'PharmacyBroker', states: ['WA', 'OR', 'ID'] },
  ])

  // Check if user has access
  const hasClinicAccess = user?.role === 'super_admin' || (currentClinicId && user?.clinicIds?.includes(currentClinicId))

  useEffect(() => {
    if (hasClinicAccess) {
      fetchPharmacyDetails()
      fetchPharmacyPrograms()
    }
  }, [id, hasClinicAccess])

  const fetchPharmacyDetails = async () => {
    try {
      const response = await pharmacyAPI.get(id)
      if (response.data.success) {
        setPharmacy(response.data.data)
      }
    } catch (err) {
      console.error('Failed to fetch pharmacy details:', err)
      setError(apiHelpers.handleError(err).message)
    }
  }

  const fetchPharmacyPrograms = async () => {
    try {
      const response = await pharmacyAPI.getPrograms(id)
      
      if (response.data.success) {
        const responseData = response.data.data
        const programsData = responseData?.programs || []
        setPrograms(Array.isArray(programsData) ? programsData : [])
      } else {
        setPrograms([])
      }
    } catch (err) {
      console.error('Failed to fetch pharmacy programs:', err)
      setPrograms([])
    } finally {
      setLoading(false)
    }
  }

  const formatPrice = (price) => {
    if (!price) return 'N/A'
    return `$${(price / 100).toFixed(2)}`
  }

  const getStatusVariant = (pharmacy) => {
    if (!pharmacy?.pharmacy_config?.enable_pharmacy) return 'secondary'
    
    const config = pharmacy.pharmacy_config || {}
    const utilization = config.weekly_order_capacity ? 
      Math.round((config.current_week_orders / config.weekly_order_capacity) * 100) : 0
    
    if (utilization >= 95) return 'destructive'
    if (utilization >= 80) return 'outline'
    return 'default'
  }

  const getStatusText = (pharmacy) => {
    if (!pharmacy?.pharmacy_config?.enable_pharmacy) return 'Disabled'
    
    const config = pharmacy.pharmacy_config || {}
    const utilization = config.weekly_order_capacity ? 
      Math.round((config.current_week_orders / config.weekly_order_capacity) * 100) : 0
    
    if (utilization >= 95) return 'At Capacity'
    if (utilization >= 80) return 'Near Capacity'
    return 'Available'
  }

  // Helper function to get state abbreviation from state name
  const getStateAbbreviation = (stateName) => {
    const stateMap = {
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
      'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
      'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
      'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
      'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
      'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
      'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
      'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
      'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
    }
    return stateMap[stateName] || stateName
  }

  // Determine license info for a state based on granular fields
  const getStateLicenseInfo = (stateCode) => {
    const hasControlled = pharmacy.states_controlled?.includes(stateCode)
    const hasNonSterile = pharmacy.states_non_sterile?.includes(stateCode)
    const hasSterile = pharmacy.states_sterile?.includes(stateCode)
    const hasLegacy = pharmacy.states?.includes(stateCode)
    
    // Determine base compound type
    let baseType = 'none'
    if (hasSterile) {
      baseType = 'sterile' // Green - sterile implies non-sterile
    } else if (hasNonSterile) {
      baseType = 'non_sterile' // Orange - non-sterile only
    } else if (hasLegacy) {
      baseType = 'legacy' // Fallback to legacy states field
    }
    
    return {
      baseType,
      hasControlled,
      hasSterile,
      hasNonSterile,
      hasLegacy,
      isLicensed: baseType !== 'none' || hasControlled
    }
  }

  // For backwards compatibility
  const getStateLicenseType = (stateCode) => {
    const info = getStateLicenseInfo(stateCode)
    if (info.hasControlled && (info.hasNonSterile || info.hasSterile)) return 'all'
    if (info.hasSterile) return 'sterile'
    if (info.hasNonSterile) return 'non_sterile'
    if (info.hasControlled) return 'controlled_only'
    if (info.hasLegacy) return 'legacy'
    return 'none'
  }

  const getMapStateFill = (stateCode) => {
    const info = getStateLicenseInfo(stateCode)
    
    // Use pattern for controlled states, solid color otherwise
    if (info.hasControlled) {
      if (info.hasSterile) return 'url(#stripes-green)'
      if (info.hasNonSterile) return 'url(#stripes-orange)'
      return 'url(#stripes-gray)' // Controlled only - edge case
    }
    
    // Solid colors for non-controlled
    switch (info.baseType) {
      case 'sterile': return '#16a34a' // green-600
      case 'non_sterile': return '#ea580c' // orange-600
      case 'legacy': return '#6b7280' // gray-500
      default: return '#e5e7eb' // gray-200 - not licensed
    }
  }

  const getLicenseTypeLabel = (stateCode) => {
    const info = getStateLicenseInfo(stateCode)
    const parts = []
    
    if (info.hasControlled) parts.push('Controlled (DEA)')
    if (info.hasSterile) parts.push('Sterile')
    else if (info.hasNonSterile) parts.push('Non-Sterile')
    else if (info.hasLegacy) parts.push('Legacy')
    
    if (parts.length === 0) return 'Not Licensed'
    return parts.join(' + ')
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return dateString
    }
  }

  // Get ingredient price (the important one) from nested drugs data
  const getIngredientPrice = (program) => {
    return program.pharmacy_specific?.drugs?.[0]?.current_price || 0
  }
  
  // Get shipping/handling price (less important)
  const getShippingPrice = (program) => {
    return program.pharmacy_specific?.current_price || 0
  }

  // Get drug details from nested data
  const getDrugDetails = (program) => {
    const drugs = program.pharmacy_specific?.drugs || []
    return drugs[0] || {} // Get first drug for display
  }

  // Filter and search programs
  const filteredPrograms = Array.isArray(programs) ? programs.filter(program => {
    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      const matchesName = program.drug_name?.toLowerCase().includes(searchLower)
      const matchesEntity = program.entity_id?.toLowerCase().includes(searchLower)
      const matchesMarketing = program.marketing_name?.toLowerCase().includes(searchLower)
      if (!matchesName && !matchesEntity && !matchesMarketing) return false
    }

    // DEA filter
    if (showDeaOnly && !program.dea) return false

    // Tag filter
    if (selectedTag && (!program.tags || !program.tags.includes(selectedTag))) return false

    return true
  }) : []

  // Get unique tags from all programs
  const allTags = Array.isArray(programs) ? 
    [...new Set(programs.flatMap(p => p.tags || []))].sort() : []

  // Modal handlers
  const handlePharmacySave = (updatedPharmacy) => {
    console.log('Pharmacy saved:', updatedPharmacy)
    setPharmacy(updatedPharmacy)
    // TODO: Update backend and refresh data
  }

  const handleProgramClone = (cloneRequest) => {
    console.log('Program clone requested:', cloneRequest)
    // TODO: Send clone request to backend
    // TODO: Show success notification
    // TODO: Refresh program data
  }

  const openCloneProgramModal = (program) => {
    setCloneProgramModal({ isOpen: true, program })
  }

  const closeCloneProgramModal = () => {
    setCloneProgramModal({ isOpen: false, program: null })
  }

  if (!hasClinicAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Clinic Selection Required</CardTitle>
            <CardDescription>Please select a clinic from the header dropdown to view pharmacy details.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading pharmacy details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Error Loading Pharmacy</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/pharmacy')}>
                Back to Pharmacies
              </Button>
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!pharmacy) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Pharmacy Not Found</CardTitle>
            <CardDescription>The requested pharmacy could not be found.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate('/pharmacy')}>
              Back to Pharmacies
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const config = pharmacy.pharmacy_config || {}
  const utilization = config.weekly_order_capacity ? 
    Math.round((config.current_week_orders / config.weekly_order_capacity) * 100) : 0

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/pharmacy')}
                className="flex items-center gap-2"
              >
                ← Back to Pharmacies
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-foreground">{pharmacy.pharmacy_name}</h1>
                <p className="text-muted-foreground mt-1">
                  {pharmacy.pharmacy_ncpdpid ? `NCPDP ID: ${pharmacy.pharmacy_ncpdpid}` : 'Pharmacy Details'}
                </p>
              </div>
            </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getStatusVariant(pharmacy)}>
                      {getStatusText(pharmacy)}
                    </Badge>
                    <Button 
                      variant="outline" 
                      onClick={() => setIsEditModalOpen(true)}
                    >
                      Edit Pharmacy
                    </Button>
                  </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Weekly Capacity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{config.weekly_order_capacity || 0}</div>
              <p className="text-xs text-muted-foreground">
                {config.current_week_orders || 0} orders this week ({utilization}%)
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Available Programs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{filteredPrograms.length}</div>
              <p className="text-xs text-muted-foreground">
                Drug programs offered
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Gateway</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pharmacy.pharmacy_gateway || 'N/A'}</div>
              <p className="text-xs text-muted-foreground">
                Processing gateway
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">States Served</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(() => {
                  // Count unique states across all license types
                  const allStates = new Set([
                    ...(pharmacy.states || []),
                    ...(pharmacy.states_controlled || []),
                    ...(pharmacy.states_non_sterile || []),
                    ...(pharmacy.states_sterile || [])
                  ])
                  return allStates.size
                })()}
              </div>
              <p className="text-xs text-muted-foreground">
                {pharmacy.states_controlled?.length || 0} controlled, {pharmacy.states_sterile?.length || 0} sterile, {pharmacy.states_non_sterile?.length || 0} non-sterile
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pharmacy Information */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Pharmacy Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Address */}
                <div>
                  <h4 className="font-medium mb-2">Address</h4>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>{pharmacy.address?.address}</p>
                    <p>{pharmacy.address?.city}, {pharmacy.address?.state} {pharmacy.address?.zipcode}</p>
                    {pharmacy.address?.country && <p>{pharmacy.address?.country}</p>}
                  </div>
                </div>

                <Separator />

                {/* Contact Information */}
                <div>
                  <h4 className="font-medium mb-2">Contact</h4>
                  <div className="text-sm text-muted-foreground space-y-1">
                    {pharmacy.phone_number && <p>Phone: {pharmacy.phone_number}</p>}
                    {pharmacy.fax && <p>Fax: {pharmacy.fax}</p>}
                    {pharmacy.contact_emails && pharmacy.contact_emails.length > 0 && (
                      <p>Email: {pharmacy.contact_emails[0]}</p>
                    )}
                  </div>
                </div>

                <Separator />

                {/* IDs & Licensing */}
                <div>
                  <h4 className="font-medium mb-2">Identifiers</h4>
                  <div className="text-sm text-muted-foreground space-y-1">
                    {pharmacy.pharmacy_ncpdpid && (
                      <p>NCPDP ID: {pharmacy.pharmacy_ncpdpid}</p>
                    )}
                    {pharmacy.dosespot_pharmacy_id && (
                      <p>DoseSpot ID: {pharmacy.dosespot_pharmacy_id}</p>
                    )}
                    {pharmacy.npi && <p>NPI: {pharmacy.npi}</p>}
                    {pharmacy.dea && <p>DEA: {pharmacy.dea}</p>}
                  </div>
                </div>

              </CardContent>
            </Card>

            {/* State Coverage Map */}
            <Card>
              <CardHeader>
                <CardTitle>State Coverage</CardTitle>
                <CardDescription>
                  Geographic coverage and licensing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Legend */}
                <div className="flex flex-wrap gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3 rounded" style={{ backgroundColor: '#16a34a' }}></div>
                    <span>Sterile</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3 rounded" style={{ backgroundColor: '#ea580c' }}></div>
                    <span>Non-Sterile</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <svg width="16" height="12" className="rounded overflow-hidden">
                      <defs>
                        <pattern id="legend-stripes" patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(45)">
                          <rect width="4" height="4" fill="#16a34a" />
                          <line x1="0" y1="0" x2="0" y2="4" stroke="#064e3b" strokeWidth="2" />
                        </pattern>
                      </defs>
                      <rect width="16" height="12" fill="url(#legend-stripes)" />
                    </svg>
                    <span>+ DEA (stripes)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3 rounded" style={{ backgroundColor: '#6b7280' }}></div>
                    <span>Legacy</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3 rounded" style={{ backgroundColor: '#e5e7eb' }}></div>
                    <span>Not Licensed</span>
                  </div>
                </div>
                
                {/* US State Map */}
                <div className="relative border rounded-lg p-4 bg-slate-50 dark:bg-slate-900/50">
                  <ComposableMap 
                    projection="geoAlbersUsa"
                    className="w-full h-48"
                    width={800}
                    height={400}
                  >
                    {/* SVG Pattern definitions for DEA/Controlled texture */}
                    <defs>
                      <pattern id="stripes-green" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                        <rect width="8" height="8" fill="#16a34a" />
                        <line x1="0" y1="0" x2="0" y2="8" stroke="#064e3b" strokeWidth="3" />
                      </pattern>
                      <pattern id="stripes-orange" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                        <rect width="8" height="8" fill="#ea580c" />
                        <line x1="0" y1="0" x2="0" y2="8" stroke="#7c2d12" strokeWidth="3" />
                      </pattern>
                      <pattern id="stripes-gray" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                        <rect width="8" height="8" fill="#9ca3af" />
                        <line x1="0" y1="0" x2="0" y2="8" stroke="#374151" strokeWidth="3" />
                      </pattern>
                    </defs>
                    <Geographies geography={geoUrl}>
                      {({ geographies }) =>
                        geographies.map((geo) => {
                          const stateName = geo.properties.NAME
                          const stateCode = getStateAbbreviation(stateName)
                          const info = getStateLicenseInfo(stateCode)
                          const fill = getMapStateFill(stateCode)
                          
                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              fill={fill}
                              stroke="#374151"
                              strokeWidth={0.5}
                              onMouseEnter={() => {
                                setHoveredState({ 
                                  name: stateName, 
                                  code: stateCode, 
                                  licenseLabel: getLicenseTypeLabel(stateCode)
                                })
                              }}
                              onMouseLeave={() => {
                                setHoveredState(null)
                              }}
                              style={{
                                default: {
                                  outline: "none",
                                },
                                hover: {
                                  fill: info.isLicensed ? fill : '#94a3b8',
                                  stroke: "#1f2937",
                                  strokeWidth: 2,
                                  outline: "none",
                                },
                                pressed: {
                                  fill: fill,
                                  stroke: "#374151",
                                  strokeWidth: 2,
                                  outline: "none",
                                },
                              }}
                            />
                          )
                        })
                      }
                    </Geographies>
                  </ComposableMap>
                  
                  {/* Tooltip */}
                  {hoveredState && (
                    <div className="absolute top-2 left-2 bg-black text-white px-2 py-1 rounded text-xs font-medium z-10">
                      {hoveredState.name} ({hoveredState.code}) - {hoveredState.licenseLabel}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Drug Programs */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Available Drug Programs ({filteredPrograms.length})</CardTitle>
                    <CardDescription>
                      Programs offered by this pharmacy with pricing and details
                    </CardDescription>
                  </div>
                  {filteredPrograms.length > 0 && (
                    <div className="flex gap-2 text-xs text-muted-foreground">
                      <div className="text-center">
                        <div className="font-medium text-foreground">{filteredPrograms.filter(p => p.dea).length}</div>
                        <div>DEA Required</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-foreground">{filteredPrograms.filter(p => p.fill_schedule).length}</div>
                        <div>Phased</div>
                      </div>
                      {/* Drug costs are what the pharmacy charges us, and are
                          withheld from the clinic-facing roles. The API strips
                          them too — see backend/src/utils/drugPricing.js. */}
                      {showPrices && (
                        <div className="text-center">
                          <div className="font-medium text-primary">
                            {(() => {
                              const validPrices = filteredPrograms.filter(p => getIngredientPrice(p) > 0).map(p => getIngredientPrice(p) / 100)
                              return validPrices.length > 0 
                                ? `$${Math.min(...validPrices).toFixed(0)}-${Math.max(...validPrices).toFixed(0)}`
                                : 'N/A'
                            })()}
                          </div>
                          <div>Ingredient Price</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Search and Filter Controls */}
                <div className="mb-6 space-y-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <Input
                        placeholder="Search programs by name, entity ID, or marketing name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Select value={selectedTag || 'all'} onValueChange={(value) => setSelectedTag(value === 'all' ? '' : value)}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Filter by tag" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Tags</SelectItem>
                          {allTags.map((tag) => (
                            <SelectItem key={tag} value={tag}>
                              {tag}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="dea-only"
                          checked={showDeaOnly}
                          onCheckedChange={setShowDeaOnly}
                        />
                        <label
                          htmlFor="dea-only"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          DEA Only
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Filter Results Summary */}
                  {(searchQuery || selectedTag || showDeaOnly) && (
                    <div className="text-sm text-muted-foreground">
                      Showing {filteredPrograms.length} of {programs.length} programs
                      {searchQuery && ` matching "${searchQuery}"`}
                      {selectedTag && ` tagged with "${selectedTag}"`}
                      {showDeaOnly && ` requiring DEA`}
                    </div>
                  )}
                </div>

                {filteredPrograms.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <div className="mb-2">💊</div>
                    <p className="font-medium">
                      {programs.length === 0 ? 'No drug programs found' : 'No programs match your filters'}
                    </p>
                    <p className="text-sm">
                      {programs.length === 0 
                        ? "This pharmacy doesn't currently offer any drug programs."
                        : 'Try adjusting your search or filter criteria.'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredPrograms.map((program, index) => {
                      const drugDetails = getDrugDetails(program)
                      const ingredientPrice = getIngredientPrice(program)
                      const shippingPrice = getShippingPrice(program)
                      
                      return (
                        <Card key={index} className="border-l-4 border-l-primary">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <CardTitle className="text-lg">
                                  <Link 
                                    to={`/drug-programs/${program._id}`}
                                    className="hover:underline text-primary"
                                  >
                                    {program.drug_name}
                                  </Link>
                                </CardTitle>
                                <CardDescription className="mt-1">
                                  {program.entity_id} • {program.dosespot_type}
                                  {program.marketing_name && ` • ${program.marketing_name}`}
                                </CardDescription>
                              </div>
                              {showPrices && (
                                <div className="text-right">
                                  <div className="text-lg font-bold text-primary">
                                    {formatPrice(ingredientPrice)}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    Ingredient{shippingPrice > 0 ? ` + ${formatPrice(shippingPrice)} ship` : ''}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {program.dea && <Badge variant="destructive" className="text-xs">DEA</Badge>}
                              {program.local_pharmacy && <Badge variant="secondary" className="text-xs">Local Only</Badge>}
                              {program.sync_required && <Badge variant="outline" className="text-xs">Sync Required</Badge>}
                              {program.fill_schedule && <Badge variant="outline" className="text-xs">Phased Program</Badge>}
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                              <div>
                                <span className="text-muted-foreground">Quantity:</span>
                                <div className="font-medium">
                                  {drugDetails.dispense_quantity || 'N/A'} {drugDetails.dispense_unit || ''}
                                </div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Type:</span>
                                <div className="font-medium capitalize">{program.dosespot_type}</div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Days Supply:</span>
                                <div className="font-medium">{drugDetails.days || 'N/A'} days</div>
                              </div>
                            </div>

                                   {drugDetails.sig && (
                                     <div className="mb-3 p-2 bg-muted rounded text-xs">
                                       <span className="font-medium">Instructions: </span>
                                       {drugDetails.sig}
                                     </div>
                                   )}

                                   <div className="flex items-start justify-between gap-4">
                                     <div className="flex-1">
                                       {program.tags && program.tags.length > 0 && (
                                         <div className="flex flex-wrap gap-1">
                                           {program.tags.slice(0, 8).map((tag, tagIndex) => (
                                             <Badge 
                                               key={tagIndex} 
                                               variant="outline" 
                                               className="text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground"
                                               onClick={() => setSelectedTag(tag)}
                                             >
                                               {tag}
                                             </Badge>
                                           ))}
                                           {program.tags.length > 8 && (
                                             <Badge variant="outline" className="text-xs">
                                               +{program.tags.length - 8} more
                                             </Badge>
                                           )}
                                         </div>
                                       )}
                                     </div>

                                     <div className="flex gap-2">
                                       <Button
                                         variant="outline"
                                         size="sm"
                                         onClick={() => openCloneProgramModal(program)}
                                         className="text-xs"
                                       >
                                         Clone Program
                                       </Button>
                                     </div>
                                   </div>
                          </CardContent>
                        </Card>
                      )
                    })}
                    
                    {filteredPrograms.length > 5 && (
                      <div className="text-center pt-4">
                        <p className="text-sm text-muted-foreground">
                          Showing all {filteredPrograms.length} programs
                          {filteredPrograms.length !== programs.length && ` (filtered from ${programs.length} total)`}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Pharmacy Modal */}
      <PharmacyEditModal
        pharmacy={pharmacy}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handlePharmacySave}
      />

      {/* Clone Program Modal */}
      <CloneProgramModal
        program={cloneProgramModal.program}
        isOpen={cloneProgramModal.isOpen}
        onClose={closeCloneProgramModal}
        onClone={handleProgramClone}
        availablePharmacies={availablePharmacies}
      />
    </div>
  )
}
